import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  Container,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  InputGroup,
  InputRightElement,
  Spinner,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
  VStack,
  Tooltip,
  RadioGroup,
  Radio,
} from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon, Search2Icon, ChevronDownIcon } from "@chakra-ui/icons";
import { quickbooksService } from "../../api/quickbooks";
import { GL_SYNC_DATES, TB_SYNC_DATES } from "../../config/syncDefaults";
import { MdOutlineTextsms } from 'react-icons/md';

// Constants for sessionStorage
const STORAGE_KEY = "lucro_bulk_qb_selected_clients";

export const BulkQuickbooksSync = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const { jobId: jobIdFromUrl } = useParams();
  const [searchParams] = useSearchParams();

  const [customers, setCustomers] = useState([]);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [activeSection, setActiveSection] = useState('changes'); // Accordion state for sync options
  
  // State for custom sync options (like in the modal)
  const [customTransactions, setCustomTransactions] = useState(false);
  const [customBalances, setCustomBalances] = useState(false);
  
  // State for Full Sync toggles
  const [fullSyncImport, setFullSyncImport] = useState(true);
  const [fullSyncFetch, setFullSyncFetch] = useState(false);
  
  // Simple sync state
  const [showLegacy, setShowLegacy] = useState(false);
  const [syncType, setSyncType] = useState('all'); // balances, transactions, all
  const [fetchMode, setFetchMode] = useState('cdc'); // full, range, cdc, none
  const [importMode, setImportMode] = useState('import'); // import, raw_only, balances_only

  const [glDates, setGlDates] = useState({
    startDate: GL_SYNC_DATES.startDate,
    endDate: GL_SYNC_DATES.endDate
  });

  const [tbDates, setTbDates] = useState({
    startDate: TB_SYNC_DATES.startDate,
    endDate: TB_SYNC_DATES.endDate
  });

  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [previousCursor, setPreviousCursor] = useState(null);

  // Handle CDC sync URL parameter
  useEffect(() => {
    const syncCdcParam = searchParams.get('sync_cdc');
    if (syncCdcParam === 'true') {
      setFetchMode('cdc');
      setShowLegacy(false);
    }
  }, [searchParams]);

  // Load previously selected clients from sessionStorage
  useEffect(() => {
    const savedSelections = sessionStorage.getItem(STORAGE_KEY);
    if (savedSelections) {
      try {
        const parsedSelections = JSON.parse(savedSelections);
        if (Array.isArray(parsedSelections)) {
          setSelectedCustomers(parsedSelections);
        }
      } catch (e) {
        console.error("Error loading saved client selections:", e);
        // Clear corrupted data
        sessionStorage.removeItem(STORAGE_KEY);
      }
    }
  }, []);

  // Save selected clients to sessionStorage whenever they change
  useEffect(() => {
    if (selectedCustomers.length > 0) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(selectedCustomers));
    }
  }, [selectedCustomers]);

  // Fetch customers with pagination and search
  const refreshCustomersList = useCallback(
    (cursor) => {
      setLoading(true);

      const fetchData = async () => {
        try {
          // Prepare query parameters
          let params = {};
          if (cursor) {
            const urlObj = new URL(cursor);
            params.cursor = urlObj.searchParams.get("cursor");
          }

          if (search) {
            params.search = search;
          }
          // Always include notes in customer fetches
          params.include_notes = true;

          // Get customers with active QuickBooks connections with pagination
          const response = await quickbooksService.getCustomers(1, params, { quickbooksConnectionStatus: 'connected' });

          // Extract data and pagination info
          const connectedCustomers = response.data.results || [];
          setCustomers(connectedCustomers);
          setNextCursor(response.data.next);
          setPreviousCursor(response.data.previous);
        } catch (error) {
          console.error("Error fetching QuickBooks connected customers:", error);
          toast({
            title: "Error",
            description: "Failed to load customers with QuickBooks connections.",
            status: "error",
            duration: 10000,
            isClosable: true,
          });
        } finally {
          setLoading(false);
        }
      };

      fetchData();
    },
    [search, toast]
  );

  useEffect(() => {
    refreshCustomersList();
  }, [refreshCustomersList]);

  // If jobId is provided in URL, navigate to SyncJobDetails
  useEffect(() => {
    if (jobIdFromUrl) {
      navigate(`/sync-jobs/${jobIdFromUrl}`, { replace: true });
    }
  }, [jobIdFromUrl, navigate]);

  const handleSearch = (e) => {
    setSearch(e.target.value);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    refreshCustomersList();
  };

  // Handle changes to GL dates
  const handleGlDateChange = (field, value) => {
    const newGlDates = { ...glDates, [field]: value };
    setGlDates(newGlDates);
  };

  // Handle changes to TB dates
  const handleTbDateChange = (field, value) => {
    const newTbDates = { ...tbDates, [field]: value };
    setTbDates(newTbDates);
  };

  const handleSelectCustomer = (customerId) => {
    setSelectedCustomers(prev => {
      if (prev.includes(customerId)) {
        return prev.filter(id => id !== customerId);
      } else {
        return [...prev, customerId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedCustomers.length === customers.length) {
      setSelectedCustomers([]);
    } else {
      setSelectedCustomers(customers.map(customer => customer.id));
    }
  };

  const handleCancel = () => {
    navigate("/account-management");
  };

  const performBulkSync = async () => {
    // Validation
    if (selectedCustomers.length === 0) {
      toast({
        title: "Error",
        description: "Please select at least one customer.",
        status: "error",
        duration: 10000,
        isClosable: true,
      });
      return;
    }

    // Date validation for custom mode
    if (activeSection === 'custom') {
      if (!customTransactions && !customBalances) {
        toast({
          title: "Error",
          description: "Please select at least one option (Balances or Transactions) for custom sync.",
          status: "error",
          duration: 10000,
          isClosable: true,
        });
        return;
      }
      
      if (customTransactions && (!glDates.startDate || !glDates.endDate)) {
        toast({
          title: "Error",
          description: "Please specify both start and end dates for transactions.",
          status: "error",
          duration: 10000,
          isClosable: true,
        });
        return;
      }
      
      if (customBalances && (!tbDates.startDate || !tbDates.endDate)) {
        toast({
          title: "Error",
          description: "Please specify both start and end dates for balances.",
          status: "error",
          duration: 10000,
          isClosable: true,
        });
        return;
      }
    }

    setSyncing(true);

    try {
      // Create request payload based on the active section (like the modal)
      let payload = {
        customer_ids: selectedCustomers,
      };

      if (activeSection === 'changes') {
        // Changes Only section
        payload.syncType = 'transactions';
        payload.fetchMode = 'cdc';
        payload.importMode = 'import';
      } else if (activeSection === 'full') {
        // Full Sync section
        let importModeValue = 'import';
        if (fullSyncFetch && !fullSyncImport) {
          importModeValue = 'raw_only';
        } else if (!fullSyncFetch && !fullSyncImport) {
          importModeValue = 'none';
        }
        
        payload.syncType = 'all';
        payload.fetchMode = 'full';
        payload.importMode = importModeValue;
      } else if (activeSection === 'custom') {
        // Custom section - determine syncType based on checkboxes
        let syncTypeValue = 'all';
        if (customTransactions && !customBalances) {
          syncTypeValue = 'transactions';
        } else if (customBalances && !customTransactions) {
          syncTypeValue = 'balances';
        }
        
        payload.syncType = syncTypeValue;
        payload.fetchMode = 'range';
        payload.importMode = 'import';
        
        // Add date fields for custom mode
        if (customTransactions) {
          payload.gl_start_date = glDates.startDate;
          payload.gl_end_date = glDates.endDate;
        }
        if (customBalances) {
          payload.tb_start_date = tbDates.startDate;
          payload.tb_end_date = tbDates.endDate;
        }
      }

      // Log request payload for debugging
      console.log('Bulk sync request payload:', payload);

      const response = await quickbooksService.syncQuickbooksData(payload);

      // Log the entire response for debugging
      console.log('Bulk sync response:', response);

      // Make sure we have a valid job ID before proceeding
      if (response.data && response.data.job_id) {
        const jobId = response.data.job_id;

        // Navigate to SyncJobDetails instead of staying on this page
        navigate(`/sync-jobs/${jobId}`, { replace: true });

        toast({
          title: "Sync Started",
          description: "Bulk sync job has been initiated. Please wait...",
          status: "info",
          duration: 10000,
          isClosable: true,
        });
      } else {
        console.error('No job ID returned from bulk sync API', response);
        setSyncing(false); // Reset syncing state to enable the button

        toast({
          title: "Error",
          description: "Sync was initiated but no job ID was returned. Check console for details.",
          status: "warning",
          duration: 10000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error starting bulk sync:", error);
      setSyncing(false); // Reset syncing state to enable the button

      // More detailed error handling
      let errorMessage = "An error occurred while starting the sync. Please try again.";
      if (error.response && error.response.data && error.response.data.error) {
        errorMessage = error.response.data.error;
      }

      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 10000,
        isClosable: true,
      });
    }
  };

  // Only render the sync initiation view since job details are handled by SyncJobDetails
  return (
    <SyncInitiationView
      customers={customers}
      selectedCustomers={selectedCustomers}
      showLegacy={showLegacy}
      syncType={syncType}
      setSyncType={setSyncType}
      fetchMode={fetchMode}
      setFetchMode={setFetchMode}
      importMode={importMode}
      setImportMode={setImportMode}
      setShowLegacy={setShowLegacy}
      activeSection={activeSection}
      setActiveSection={setActiveSection}
      customTransactions={customTransactions}
      setCustomTransactions={setCustomTransactions}
      customBalances={customBalances}
      setCustomBalances={setCustomBalances}
      fullSyncImport={fullSyncImport}
      setFullSyncImport={setFullSyncImport}
      fullSyncFetch={fullSyncFetch}
      setFullSyncFetch={setFullSyncFetch}
      glDates={glDates}
      tbDates={tbDates}
      syncing={syncing}
      search={search}
      handleSelectCustomer={handleSelectCustomer}
      handleSelectAll={handleSelectAll}
      handleGlDateChange={handleGlDateChange}
      handleTbDateChange={handleTbDateChange}
      handleSearch={handleSearch}
      handleSearchSubmit={handleSearchSubmit}
      performBulkSync={performBulkSync}
      handleCancel={handleCancel}
      refreshCustomersList={refreshCustomersList}
      nextCursor={nextCursor}
      previousCursor={previousCursor}
      loading={loading}
    />
  );
};

const SyncInitiationView = ({
    customers, selectedCustomers, showLegacy, syncType, setSyncType, fetchMode, setFetchMode, importMode, setImportMode, setShowLegacy, activeSection, setActiveSection, customTransactions, setCustomTransactions, customBalances, setCustomBalances, fullSyncImport, setFullSyncImport, fullSyncFetch, setFullSyncFetch, glDates, tbDates, syncing, search,
    handleSelectCustomer, handleSelectAll, handleGlDateChange, handleTbDateChange,
    handleSearch, handleSearchSubmit, performBulkSync, handleCancel,
    refreshCustomersList, nextCursor, previousCursor, loading
  }) => {
  return (
    <Container maxW="container.xl" pt={"80px"}>
      <Stack py={8}>
        <Card>
        <CardHeader pb={0}>
          <Flex justify="space-between" align="center">
            <Box>
              <Heading size="lg">Bulk QuickBooks Sync</Heading>
              <Text mt={2} color="gray.600">
                Synchronize data for multiple clients at once
              </Text>
            </Box>
          </Flex>
        </CardHeader>

        <CardBody>
          <VStack spacing={6} align="stretch" pt={4}>
            {/* Step 1: Select Customers */}
            <Box>
              <Heading size="md" mb={4}>Step 1: Select Customers</Heading>
              <HStack mb={4}>
                <form onSubmit={handleSearchSubmit} style={{ width: '100%' }}>
                  <InputGroup>
                    <Input
                      placeholder="Search customers..."
                      value={search}
                      onChange={handleSearch}
                    />
                    <InputRightElement>
                      <Button type="submit" variant="ghost" aria-label="Search">
                        <Search2Icon />
                      </Button>
                    </InputRightElement>
                  </InputGroup>
                </form>
              </HStack>

              <Box maxH="400px" overflowY="auto" borderWidth={1} borderRadius="md">
                <Table variant="simple">
                  <Thead position="sticky" top={0} bg="white" zIndex={1}>
                    <Tr>
                      <Th width="50px">
                        <Checkbox
                          isChecked={
                            customers.length > 0 &&
                            selectedCustomers.length === customers.length
                          }
                          isIndeterminate={
                            selectedCustomers.length > 0 &&
                            selectedCustomers.length < customers.length
                          }
                          onChange={handleSelectAll}
                        />
                      </Th>
                      <Th>Account</Th>
                      <Th>Note</Th>
                      <Th>Email Address</Th>
                      <Th>Primary Contact</Th>
                      <Th>Last Synced</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {loading ? (
                      <Tr>
                        <Td colSpan={6} textAlign="center">
                          <Spinner size="md" />
                        </Td>
                      </Tr>
                    ) : customers.length === 0 ? (
                      <Tr>
                        <Td colSpan={6} textAlign="center">
                          No customers found with QuickBooks connections.
                        </Td>
                      </Tr>
                    ) : (
                      customers.map((customer) => (
                        <Tr key={customer.id}>
                          <Td>
                            <Checkbox
                              isChecked={selectedCustomers.includes(customer.id)}
                              onChange={() => handleSelectCustomer(customer.id)}
                            />
                          </Td>
                          <Td>{customer.name}</Td>
                          <Td>
                            {customer.note && (typeof customer.note === 'string' ? customer.note : customer.note?.note) ? (
                              <Tooltip label="Has Note">
                                <span><MdOutlineTextsms color="#ED8936" size={20} /></span>
                              </Tooltip>
                            ) : null}
                          </Td>
                          <Td>{customer.email || ''}</Td>
                          <Td>{customer.primary_contact || ''}</Td>
                          <Td>
                            {customer.quickbooks_last_sync
                              ? new Date(
                                  customer.quickbooks_last_sync
                                ).toLocaleString()
                              : "Never"}
                          </Td>
                        </Tr>
                      ))
                    )}
                  </Tbody>
                </Table>
              </Box>

              {/* Pagination Controls */}
              <Flex justify="center" mt={4}>
                <Button
                  leftIcon={<ChevronLeftIcon />}
                  onClick={() => refreshCustomersList(previousCursor)}
                  isDisabled={!previousCursor || loading}
                  size="sm"
                  mr={2}
                >
                  Previous
                </Button>
                <Button
                  rightIcon={<ChevronRightIcon />}
                  onClick={() => refreshCustomersList(nextCursor)}
                  isDisabled={!nextCursor || loading}
                  size="sm"
                >
                  Next
                </Button>
              </Flex>

              <Text fontSize="sm" mt={2}>
                {selectedCustomers.length} customers selected
              </Text>
            </Box>

            {/* Step 2: Configure Sync Options */}
            <Box>
              <Heading size="md" mb={4}>Step 2: Configure Sync Options</Heading>

              {/* Dropdown sections: only one visible at a time */}
              <Box>
                <Text fontWeight="bold" mb={2}>Sync Options</Text>
                
                {/* Changes Only Section */}
                <Box border="1px" borderColor="gray.200" borderRadius="md" mb={2}>
                  <Button
                    width="100%"
                    justifyContent="space-between"
                    variant="ghost"
                    py={2}
                    onClick={() => {
                      setActiveSection(activeSection === 'changes' ? '' : 'changes');
                      if (activeSection !== 'changes') {
                        setSyncType('transactions');
                        setFetchMode('cdc');
                        setImportMode('import');
                      }
                    }}
                  >
                    <Box flex="1" textAlign="left" color="blue.500" fontWeight="medium" fontSize="sm">Changes Only</Box>
                    <ChevronDownIcon color="blue.500" transform={activeSection === 'changes' ? 'rotate(180deg)' : 'rotate(0deg)'} />
                  </Button>
                  {activeSection === 'changes' && (
                    <Box p={4} borderTop="1px" borderColor="gray.200">
                      <Text fontWeight="medium" mb={2}>Sync new/updated transactions changes within the CDC lookback window (30 days).</Text>
                    </Box>
                  )}
                </Box>

                {/* Full Sync Section */}
                <Box border="1px" borderColor="gray.200" borderRadius="md" mb={2}>
                  <Button
                    width="100%"
                    justifyContent="space-between"
                    variant="ghost"
                    py={2}
                    onClick={() => {
                      setActiveSection(activeSection === 'full' ? '' : 'full');
                      if (activeSection !== 'full') {
                        setSyncType('all');
                        setFetchMode('full');
                        setImportMode('import');
                      }
                    }}
                  >
                    <Box flex="1" textAlign="left" color="blue.500" fontWeight="medium" fontSize="sm">Full Sync</Box>
                    <ChevronDownIcon color="blue.500" transform={activeSection === 'full' ? 'rotate(180deg)' : 'rotate(0deg)'} />
                  </Button>
                  {activeSection === 'full' && (
                    <Box p={4} borderTop="1px" borderColor="gray.200">
                      <Text fontWeight="medium" mb={3}>Sync full QuickBooks history for all customers.</Text>
                      <VStack align="start" spacing={3}>
                        <Box>
                          <Checkbox
                            isChecked={fullSyncFetch}
                            onChange={(e) => setFullSyncFetch(e.target.checked)}
                          >
                            Fetch
                          </Checkbox>
                          <Text fontSize="sm" color="gray.600" ml={6}>
                            Download and store raw data only
                          </Text>
                        </Box>
                        <Box>
                          <Checkbox
                            isChecked={fullSyncImport}
                            onChange={(e) => setFullSyncImport(e.target.checked)}
                          >
                            Import
                          </Checkbox>
                          <Text fontSize="sm" color="gray.600" ml={6}>
                            Process and import data into journal entries
                          </Text>
                        </Box>
                      </VStack>
                    </Box>
                  )}
                </Box>

                {/* Custom Section */}
                <Box border="1px" borderColor="gray.200" borderRadius="md" mb={2}>
                  <Button
                    width="100%"
                    justifyContent="space-between"
                    variant="ghost"
                    py={2}
                    onClick={() => {
                      setActiveSection(activeSection === 'custom' ? '' : 'custom');
                    }}
                  >
                    <Box flex="1" textAlign="left" color="blue.500" fontWeight="medium" fontSize="sm">Custom</Box>
                    <ChevronDownIcon color="blue.500" transform={activeSection === 'custom' ? 'rotate(180deg)' : 'rotate(0deg)'} />
                  </Button>
                  {activeSection === 'custom' && (
                    <Box p={4} borderTop="1px" borderColor="gray.200">
                      <VStack align="stretch" spacing={3}>
                        <Text fontWeight="medium" mb={2}>Select data to sync:</Text>
                        <VStack align="start" spacing={3}>
                          <Box>
                            <Checkbox
                              isChecked={customBalances}
                              onChange={(e) => setCustomBalances(e.target.checked)}
                              mb={2}
                            >
                              Balances
                            </Checkbox>
                            <Box ml={6} opacity={customBalances ? 1 : 0.5}>
                              <Text fontWeight="medium" mb={2} fontSize="sm">Trial Balance Date Range</Text>
                              <HStack spacing={3}>
                                <Input 
                                  type="date" 
                                  value={tbDates.startDate} 
                                  onChange={e => handleTbDateChange('startDate', e.target.value)} 
                                  size="sm" 
                                  isDisabled={!customBalances}
                                />
                                <Text fontSize="sm" color="gray.500">to</Text>
                                <Input 
                                  type="date" 
                                  value={tbDates.endDate} 
                                  onChange={e => handleTbDateChange('endDate', e.target.value)} 
                                  size="sm" 
                                  isDisabled={!customBalances}
                                />
                              </HStack>
                            </Box>
                          </Box>
                          
                          <Box>
                            <Checkbox
                              isChecked={customTransactions}
                              onChange={(e) => setCustomTransactions(e.target.checked)}
                              mb={2}
                            >
                              Transactions
                            </Checkbox>
                            <Box ml={6} opacity={customTransactions ? 1 : 0.5}>
                              <Text fontWeight="medium" mb={2} fontSize="sm">General Ledger Date Range</Text>
                              <HStack spacing={3}>
                                <Input 
                                  type="date" 
                                  value={glDates.startDate} 
                                  onChange={e => handleGlDateChange('startDate', e.target.value)} 
                                  size="sm" 
                                  isDisabled={!customTransactions}
                                />
                                <Text fontSize="sm" color="gray.500">to</Text>
                                <Input 
                                  type="date" 
                                  value={glDates.endDate} 
                                  onChange={e => handleGlDateChange('endDate', e.target.value)} 
                                  size="sm" 
                                  isDisabled={!customTransactions}
                                />
                              </HStack>
                            </Box>
                          </Box>
                        </VStack>
                      </VStack>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>

            <Divider />

            {/* Actions */}
            <Flex justify="flex-end" gap={4} pt={2}>
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                onClick={performBulkSync}
                isLoading={syncing}
                loadingText="Syncing..."
                isDisabled={
                  selectedCustomers.length === 0 ||
                  (activeSection === 'custom' && !customTransactions && !customBalances) ||
                  (activeSection === 'custom' && customTransactions && (!glDates.startDate || !glDates.endDate)) ||
                  (activeSection === 'custom' && customBalances && (!tbDates.startDate || !tbDates.endDate))
                }
              >
                Start Sync
              </Button>
            </Flex>
          </VStack>
        </CardBody>
      </Card>
      </Stack>
    </Container>
  );
};
